import { useId, useState } from 'react'
import { Button, Dialog } from '@bloo-team/ui'
import { CM_DISPOSITIONS, CM_SEVERITIES } from '@bloo-team/api-client'
import type { CmBulkResult, CmCase, CmDisposition, CmSeverity, CmStatus } from '@bloo-team/api-client'
import { caseLabel, CATEGORY_LABEL, DISPOSITION_LABEL } from '../../lib/case-mgmt'

/**
 * Case V2 dialogs (docs/cases.md): the New case fields (Add to case on
 * Signals uses them), Close (bulk), Change status. Each remounts its form
 * per open so drafts never leak between uses; server refusals render inline. Props in, callbacks out. Every close takes
 * a disposition (Epic 20) — one per case in a bulk close.
 */

const isDisposition = (v: string | null | undefined): v is CmDisposition => (CM_DISPOSITIONS as readonly string[]).includes(v ?? '')

/** The four dispositions as toggles; `labelId` names the group. */
function DispositionToggles({ labelId, value, disabled, onPick }: { labelId: string; value: string; disabled: boolean; onPick: (v: CmDisposition) => void }) {
  return (
    <span className="bui-toggles" role="group" aria-labelledby={labelId}>
      {CM_DISPOSITIONS.map((d) => (
        <button key={d} type="button" className="bui-toggle" aria-pressed={value === d} disabled={disabled} onClick={() => onPick(d)}>
          {DISPOSITION_LABEL[d]}
        </button>
      ))}
    </span>
  )
}

/* ---------------------------------------------------------------- new case */

export interface NewCaseInput {
  title: string
  severity: CmSeverity
  description: string
  assignee: string
}

export interface NewCaseFormProps {
  /** Prefilled title (escalation: the signal's detection). */
  initialTitle?: string
  initialDescription?: string
  initialSeverity?: CmSeverity
  users: string[]
  busy: boolean
  error: string | null
  confirmLabel?: string
  onCancel: () => void
  onConfirm: (input: NewCaseInput) => void
}

/** The New case fields — also the escalate dialog's "New case" half. */
export function NewCaseForm({
  initialTitle = '',
  initialDescription = '',
  initialSeverity = 'medium',
  users,
  busy,
  error,
  confirmLabel = 'Create case',
  onCancel,
  onConfirm,
}: NewCaseFormProps) {
  const uid = useId()
  const [title, setTitle] = useState(initialTitle)
  const [severity, setSeverity] = useState<CmSeverity>(initialSeverity)
  const [description, setDescription] = useState(initialDescription)
  const [assignee, setAssignee] = useState('')
  const titleOk = title.trim().length >= 1 && title.trim().length <= 256
  return (
    <div className="hud-stack sp-form">
      <label className="hud-stack sp-field" htmlFor={`${uid}-title`}>
        <span className="hud-eyebrow">title · required</span>
        <input id={`${uid}-title`} className="bui-input" maxLength={256} value={title} disabled={busy} onChange={(e) => setTitle(e.target.value)} />
      </label>
      <div className="hud-stack sp-field">
        <span id={`${uid}-sev`} className="hud-eyebrow">
          severity · required
        </span>
        <span className="bui-toggles" role="group" aria-labelledby={`${uid}-sev`}>
          {CM_SEVERITIES.map((s) => (
            <button key={s} type="button" className="bui-toggle" aria-pressed={severity === s} disabled={busy} onClick={() => setSeverity(s)}>
              {s}
            </button>
          ))}
        </span>
      </div>
      <label className="hud-stack sp-field" htmlFor={`${uid}-desc`}>
        <span className="hud-eyebrow">description · optional</span>
        <textarea id={`${uid}-desc`} className="bui-input" rows={4} value={description} disabled={busy} onChange={(e) => setDescription(e.target.value)} />
      </label>
      <label className="hud-stack sp-field" htmlFor={`${uid}-assignee`}>
        <span className="hud-eyebrow">assignee · optional</span>
        <select id={`${uid}-assignee`} className="bui-input" value={assignee} disabled={busy} onChange={(e) => setAssignee(e.target.value)}>
          <option value="">Unassigned</option>
          {users.map((u) => (
            <option key={u} value={u}>
              {u}
            </option>
          ))}
        </select>
      </label>
      <p className="q-facet-note">New cases start as Open.</p>
      {error ? (
        <p className="sp-form-error" role="alert">
          {error}
        </p>
      ) : null}
      <div className="hud-row sp-form-actions">
        <Button variant="ghost" size="sm" tone="neutral" isDisabled={busy} onPress={onCancel}>
          Cancel
        </Button>
        <Button variant="solid" size="sm" isDisabled={!titleOk || busy} onPress={() => onConfirm({ title: title.trim(), severity, description: description.trim(), assignee })}>
          {busy ? 'Creating…' : confirmLabel}
        </Button>
      </div>
    </div>
  )
}

/* ------------------------------------------------------------- bulk close */

const BULK_ERROR: Record<string, string> = {
  not_found: 'Case not found',
  invalid_transition: 'This status cannot be reached from the current one',
  reason_required: 'Reason required',
  assignee_required: 'Assign someone first',
  note_required: 'Note required',
  internal_error: 'Server error',
}

export interface BulkCloseDialogProps {
  open: boolean
  cases: CmCase[]
  /** Closed-category statuses. */
  targets: CmStatus[]
  busy: boolean
  error: string | null
  result: CmBulkResult | null
  /** The service refused the whole request for a missing disposition (422 `disposition_required`); nothing was closed. */
  dispositionRefused?: boolean
  onCancel: () => void
  onConfirm: (input: { status: string; reason: string; dispositions: Record<string, CmDisposition> }) => void
}

export function BulkCloseDialog({ open, ...p }: BulkCloseDialogProps) {
  return (
    <Dialog open={open} onClose={p.onCancel} title={`Close ${p.cases.length} case${p.cases.length === 1 ? '' : 's'}`} className="sp-close-dialog">
      {open ? <BulkCloseForm {...p} /> : null}
    </Dialog>
  )
}

function BulkCloseForm({ cases, targets, busy, error, result, dispositionRefused, onCancel, onConfirm }: Omit<BulkCloseDialogProps, 'open'>) {
  const uid = useId()
  const [status, setStatus] = useState(targets[0]?.key ?? '')
  const [reason, setReason] = useState('')
  const [dispositions, setDispositions] = useState<Record<string, CmDisposition>>({})
  const missing = cases.filter((c) => !dispositions[c.id])
  const all = cases.length && !missing.length && cases.every((c) => dispositions[c.id] === dispositions[cases[0]!.id]) ? dispositions[cases[0]!.id]! : ''
  const title = (id: string) => cases.find((c) => c.id === id)?.title ?? id
  if (result) {
    const failed = result.results.filter((r) => !r.ok)
    return (
      <div className="hud-stack sp-form">
        <p role="status" className="mono">
          succeeded {result.summary.succeeded} · unchanged {result.summary.unchanged} · failed {result.summary.failed}
        </p>
        {failed.length ? (
          <ul className="cs-bulk-failed" aria-label="Cases not closed">
            {failed.map((r) => (
              <li key={r.caseId}>
                <span>{title(r.caseId)}</span>
                <span className="muted">{BULK_ERROR[r.errorCode ?? ''] ?? r.errorCode ?? 'Failed'}</span>
              </li>
            ))}
          </ul>
        ) : null}
        <div className="hud-row sp-form-actions">
          <Button variant="solid" size="sm" onPress={onCancel}>
            Done
          </Button>
        </div>
      </div>
    )
  }
  return (
    <div className="hud-stack sp-form">
      <div className="hud-stack sp-field" role="radiogroup" aria-labelledby={`${uid}-target`}>
        <span id={`${uid}-target`} className="hud-eyebrow">
          close as
        </span>
        {targets.map((t) => (
          <label key={t.key} className="hud-row cs-radio">
            <input type="radio" name={`${uid}-status`} value={t.key} checked={status === t.key} disabled={busy} onChange={() => setStatus(t.key)} />
            {t.label}
          </label>
        ))}
      </div>
      <div className="hud-stack sp-field">
        <span id={`${uid}-all`} className="hud-eyebrow">
          Set all to
        </span>
        <DispositionToggles labelId={`${uid}-all`} value={all} disabled={busy} onPick={(d) => setDispositions(Object.fromEntries(cases.map((c) => [c.id, d])))} />
      </div>
      <ul className="hud-stack cs-bulk-dispositions" aria-label="Disposition per case">
        {cases.map((c) => (
          <li key={c.id} className="hud-row cs-bulk-disposition">
            <span className="mono">{caseLabel(c)}</span>
            <span className="cs-bulk-title">{c.title}</span>
            <select
              aria-label={`Disposition for ${c.title}`}
              className="bui-input"
              value={dispositions[c.id] ?? ''}
              disabled={busy}
              onChange={(e) => {
                const v = e.target.value
                setDispositions(({ [c.id]: _old, ...rest }) => (isDisposition(v) ? { ...rest, [c.id]: v } : rest))
              }}
            >
              <option value="">choose…</option>
              {CM_DISPOSITIONS.map((d) => (
                <option key={d} value={d}>
                  {DISPOSITION_LABEL[d]}
                </option>
              ))}
            </select>
            {dispositions[c.id] ? null : <span className="sp-form-error">needed</span>}
          </li>
        ))}
      </ul>
      <label className="hud-stack sp-field" htmlFor={`${uid}-reason`}>
        <span className="hud-eyebrow">reason · optional</span>
        <textarea id={`${uid}-reason`} className="bui-input" rows={3} maxLength={2000} value={reason} disabled={busy} onChange={(e) => setReason(e.target.value)} />
      </label>
      {dispositionRefused ? (
        <p className="sp-form-error" role="alert">
          Every case needs a disposition. Nothing was closed.
        </p>
      ) : error ? (
        <p className="sp-form-error" role="alert">
          {error}
        </p>
      ) : null}
      <div className="hud-row sp-form-actions">
        <Button variant="ghost" size="sm" tone="neutral" isDisabled={busy} onPress={onCancel}>
          Cancel
        </Button>
        <Button variant="solid" size="sm" isDisabled={!status || missing.length > 0 || busy} onPress={() => onConfirm({ status, reason: reason.trim(), dispositions })}>
          {busy ? 'Closing…' : `Close ${cases.length}`}
        </Button>
      </div>
    </div>
  )
}

/* ---------------------------------------------------------- change status */

export interface ChangeStatusInput {
  target: string
  reason: string
  note: string
  resolutionReason: string
  rootCause: string
  /** Picked inline when the target needs an assignee and the case has none. */
  assignee: string
  /** Into a closed status: required; '' otherwise (not sent). */
  disposition: CmDisposition | ''
}

export interface ChangeStatusDialogProps {
  open: boolean
  case: CmCase
  statuses: CmStatus[]
  users: string[]
  busy: boolean
  error: string | null
  /** The service refused the close for a missing disposition (422 `disposition_required`). */
  dispositionRefused?: boolean
  onCancel: () => void
  onConfirm: (input: ChangeStatusInput) => void
}

export function ChangeStatusDialog({ open, ...p }: ChangeStatusDialogProps) {
  return (
    <Dialog open={open} onClose={p.onCancel} title="Change status" className="sp-close-dialog">
      {open ? <ChangeStatusForm {...p} /> : null}
    </Dialog>
  )
}

function ChangeStatusForm({ case: c, statuses, users, busy, error, dispositionRefused, onCancel, onConfirm }: Omit<ChangeStatusDialogProps, 'open'>) {
  const uid = useId()
  const current = statuses.find((s) => s.key === c.status)
  const next = (current?.allowedNext ?? []).map((k) => statuses.find((s) => s.key === k)).filter((s): s is CmStatus => !!s)
  const [target, setTarget] = useState('')
  const [reason, setReason] = useState('')
  const [note, setNote] = useState('')
  const [resolutionReason, setResolutionReason] = useState(c.resolutionReason ?? '')
  const [rootCause, setRootCause] = useState(c.rootCause ?? '')
  const [assignee, setAssignee] = useState('')
  // Between closed statuses the case's current disposition is the starting pick; a case closed before dispositions has none.
  const [disposition, setDisposition] = useState<CmDisposition | ''>(current?.category === 'closed' && isDisposition(c.disposition) ? c.disposition : '')
  const t = next.find((s) => s.key === target)
  const closing = t?.category === 'closed'
  const needAssignee = !!t && !c.assignee && (t.requireAssignee || t.category === 'closed')
  const missing =
    !t ||
    (t.requireReason && !reason.trim()) ||
    (t.requireNote && !note.trim()) ||
    (t.requireResolutionReason && !resolutionReason.trim()) ||
    (t.requireRootCause && !rootCause.trim()) ||
    (needAssignee && !assignee) ||
    (closing && !disposition)
  const need = (flag: boolean) => (flag ? 'required' : 'optional')
  return (
    <div className="hud-stack sp-form">
      <p className="hud-unit">
        Now: <b>{current?.label ?? c.status}</b>
        {current ? ` · ${CATEGORY_LABEL[current.category].toLowerCase()}` : ''}
      </p>
      {next.length === 0 ? (
        <p className="q-facet-note" role="note">
          No status can follow this one.
        </p>
      ) : (
        <div className="hud-stack sp-field" role="radiogroup" aria-labelledby={`${uid}-to`}>
          <span id={`${uid}-to`} className="hud-eyebrow">
            move to
          </span>
          {next.map((s) => (
            <label key={s.key} className="hud-row cs-radio">
              <input type="radio" name={`${uid}-target`} value={s.key} checked={target === s.key} disabled={busy} onChange={() => setTarget(s.key)} />
              {s.label}
              <span className="cs-cat" data-category={s.category}>
                {CATEGORY_LABEL[s.category].toLowerCase()}
              </span>
            </label>
          ))}
        </div>
      )}
      {needAssignee ? (
        <label className="hud-stack sp-field" htmlFor={`${uid}-assignee`}>
          <span className="sp-form-error" role="note">
            {t!.category === 'closed' ? 'Assign someone before closing' : `Assign someone before moving to ${t!.label}`}
          </span>
          <select id={`${uid}-assignee`} aria-label="Assignee" className="bui-input" value={assignee} disabled={busy} onChange={(e) => setAssignee(e.target.value)}>
            <option value="">choose…</option>
            {users.map((u) => (
              <option key={u} value={u}>
                {u}
              </option>
            ))}
          </select>
        </label>
      ) : null}
      {t ? (
        <>
          <label className="hud-stack sp-field" htmlFor={`${uid}-reason`}>
            <span className="hud-eyebrow">reason · {need(t.requireReason)}</span>
            <textarea id={`${uid}-reason`} className="bui-input" rows={2} maxLength={2000} value={reason} disabled={busy} onChange={(e) => setReason(e.target.value)} />
          </label>
          <label className="hud-stack sp-field" htmlFor={`${uid}-note`}>
            <span className="hud-eyebrow">note · {need(t.requireNote)} · saved as a case note</span>
            <textarea id={`${uid}-note`} className="bui-input" rows={2} maxLength={10000} value={note} disabled={busy} onChange={(e) => setNote(e.target.value)} />
          </label>
          {closing ? (
            <>
              <div className="hud-stack sp-field">
                <span id={`${uid}-disp`} className="hud-eyebrow">
                  disposition · required
                </span>
                <DispositionToggles labelId={`${uid}-disp`} value={disposition} disabled={busy} onPick={setDisposition} />
                {dispositionRefused ? (
                  <span className="sp-form-error" role="alert">
                    Pick a disposition to close the case.
                  </span>
                ) : null}
              </div>
              <label className="hud-stack sp-field" htmlFor={`${uid}-res`}>
                <span className="hud-eyebrow">resolution reason · {need(t.requireResolutionReason)}</span>
                <textarea id={`${uid}-res`} className="bui-input" rows={2} maxLength={2000} value={resolutionReason} disabled={busy} onChange={(e) => setResolutionReason(e.target.value)} />
              </label>
              <label className="hud-stack sp-field" htmlFor={`${uid}-root`}>
                <span className="hud-eyebrow">root cause · {need(t.requireRootCause)}</span>
                <textarea id={`${uid}-root`} className="bui-input" rows={2} maxLength={2000} value={rootCause} disabled={busy} onChange={(e) => setRootCause(e.target.value)} />
              </label>
            </>
          ) : null}
        </>
      ) : null}
      {error && !dispositionRefused ? (
        <p className="sp-form-error" role="alert">
          {error}
        </p>
      ) : null}
      <div className="hud-row sp-form-actions">
        <Button variant="ghost" size="sm" tone="neutral" isDisabled={busy} onPress={onCancel}>
          Cancel
        </Button>
        <Button
          variant="solid"
          size="sm"
          isDisabled={missing || busy}
          onPress={() => onConfirm({ target, reason, note, resolutionReason, rootCause, assignee, disposition: closing ? disposition : '' })}
        >
          {busy ? 'Saving…' : 'Change status'}
        </Button>
      </div>
    </div>
  )
}
